// Página Home - Diseño actualizado según mockup
// Mantiene lógica de datos original pero cambia completamente la estructura visual

import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCategories } from '../hooks/useCategories'
import { useProducts } from '../hooks/useFirestore'
import { getImageUrl } from '../services/cloudinary.service'
import { getSiteSettings } from '../services/siteSettings.service'
import './Home.css'

const appendQueryParams = (url, params) => {
  if (!url) return url
  try {
    const parsed = new URL(url)
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      parsed.searchParams.set(key, String(value))
    })
    return parsed.toString()
  } catch {
    const join = url.includes('?') ? '&' : '?'
    const query = Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      .join('&')
    return `${url}${join}${query}`
  }
}

const addAutoplayParams = (url) => {
  return appendQueryParams(url, {
    autoplay: 1,
    mute: 0,
    controls: 1,
    playsinline: 1
  })
}

const buildEmbedUrl = (url, options = {}) => {
  const { autoplay = false } = options
  const raw = String(url || '').trim()
  if (!raw) return null
  if (raw.includes('youtube.com/embed/')) return autoplay ? addAutoplayParams(raw) : raw
  const ytMatch = raw.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]+)/)
  if (ytMatch?.[1]) {
    const base = `https://www.youtube.com/embed/${ytMatch[1]}`
    return autoplay ? addAutoplayParams(base) : base
  }
  const igMatch = raw.match(/instagram\.com\/(reel|p|tv)\/([A-Za-z0-9_-]+)/)
  if (igMatch?.[1] && igMatch?.[2]) {
    const base = `https://www.instagram.com/${igMatch[1]}/${igMatch[2]}/embed`
    return autoplay ? addAutoplayParams(base) : base
  }
  const ttMatch = raw.match(/tiktok\.com\/.+\/video\/(\d+)/)
  if (ttMatch?.[1]) {
    const base = `https://www.tiktok.com/embed/v2/${ttMatch[1]}`
    return autoplay ? addAutoplayParams(base) : base
  }
  if (raw.includes('facebook.com')) {
    const base = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(raw)}&show_text=false&width=500`
    return autoplay ? addAutoplayParams(base) : base
  }
  return null
}

const resolveImageSource = (image) => {
  if (!image) return null
  if (typeof image === 'string') return image
  if (typeof image === 'object') return image.fileId || image.url || image.thumbnailUrl || null
  return null
}

const getVariantMedia = (product, transformations) => {
  const media = []
  const seen = new Set()
  const variants = Array.isArray(product?.variants) ? product.variants : []
  variants.forEach(variant => {
    const variantImages = Array.isArray(variant?.images) ? variant.images : []
    variantImages.forEach(image => {
      const source = resolveImageSource(image)
      if (!source) return
      const url = getImageUrl(source, transformations)
      const key = `image:${url}`
      if (!seen.has(key)) {
        seen.add(key)
        media.push({ type: 'image', src: url })
      }
    })
    const variantVideos = Array.isArray(variant?.videos) ? variant.videos : []
    variantVideos.forEach(video => {
      const embedUrl = buildEmbedUrl(video)
      if (!embedUrl) return
      const key = `video:${embedUrl}`
      if (!seen.has(key)) {
        seen.add(key)
        media.push({ type: 'video', src: embedUrl })
      }
    })
  })
  if (media.length === 0) {
    const fallbackImages = Array.isArray(product?.images) ? product.images : []
    fallbackImages.forEach(image => {
      const source = resolveImageSource(image)
      if (!source) return
      const url = getImageUrl(source, transformations)
      const key = `image:${url}`
      if (!seen.has(key)) {
        seen.add(key)
        media.push({ type: 'image', src: url })
      }
    })
  }
  return media
}

function VariantMediaCarousel({ media, alt }) {
  const safeMedia = useMemo(() => (media.length > 0 ? media : [{ type: 'image', src: getImageUrl(null) }]), [media])
  const [index, setIndex] = useState(0)

  useEffect(() => {
    setIndex(0)
  }, [media])

  useEffect(() => {
    if (safeMedia.length <= 1) return
    const id = setInterval(() => {
      setIndex(prev => (prev + 1) % safeMedia.length)
    }, 3500)
    return () => clearInterval(id)
  }, [safeMedia])

  return (
    <div className="product-image-carousel">
      <div
        className="product-image-carousel-track"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {safeMedia.map((item, idx) => (
          <div key={`${item.type}-${item.src}-${idx}`} className="product-image-carousel-item">
            {item.type === 'video' ? (
              <iframe
                src={item.src}
                title={`Video ${idx + 1}`}
                className="product-image-carousel-video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            ) : (
              <img src={item.src} alt={alt} className="product-image-carousel-image" loading="lazy" />
            )}
          </div>
        ))}
      </div>
      {safeMedia.length > 1 && (
        <div className="product-image-carousel-dots">
          {safeMedia.map((_, idx) => (
            <button
              key={idx}
              type="button"
              className={`product-image-carousel-dot ${idx === index ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIndex(idx)
              }}
              aria-label={`Imagen ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Página Home
 * Diseño basado en mockup "Pine & Co"
 */
function Home() {
  const navigate = useNavigate()
  const { categories } = useCategories(false)
  
  // Obtener productos activos
  const { products: allProducts, loading: productsLoading } = useProducts({}, [])

  // Estado para configuración del Hero
  const [heroSettings, setHeroSettings] = useState(null)

  // Cargar configuración del Hero
  useEffect(() => {
    const loadHeroSettings = async () => {
      try {
        const settings = await getSiteSettings()
        setHeroSettings(settings?.hero || null)
      } catch (error) {
        console.error('Error cargando configuración del Hero:', error)
      }
    }

    loadHeroSettings()
  }, [])

  // Procesar productos para las secciones
  const { newArrivals, offers } = useMemo(() => {
    if (!allProducts || allProducts.length === 0) return { newArrivals: [], offers: [] }

    const activeProducts = allProducts.filter(p => p.active !== false)
    
    // Simular "Recién Llegados" (tomar los últimos añadidos si hubiera fecha, o aleatorios)
    // Aquí tomamos los primeros 4 para el ejemplo
    const newArrivals = [...activeProducts].slice(0, 4)

    // Simular "Ofertas" (productos con descuento si existiera campo, o siguientes 4)
    // Idealmente filtraríamos por p.discountPrice < p.price
    const offers = [...activeProducts].slice(4, 8)

    return { newArrivals, offers }
  }, [allProducts])

  // Manejar clic en categoría
  const handleCategoryClick = (category) => {
    navigate(`/productos?categoria=${category.slug || category.id}`)
  }

  const heroImages = useMemo(() => {
    if (Array.isArray(heroSettings?.backgroundImages)) {
      return heroSettings.backgroundImages.filter(Boolean)
    }
    return heroSettings?.backgroundImage ? [heroSettings.backgroundImage] : []
  }, [heroSettings?.backgroundImages, heroSettings?.backgroundImage])
  const heroVideos = useMemo(() => {
    if (!Array.isArray(heroSettings?.embeddedVideos)) return []
    return heroSettings.embeddedVideos.filter(Boolean)
  }, [heroSettings?.embeddedVideos])
  const heroFallbackImage = heroImages[0] || heroSettings?.backgroundImage || 'https://lh3.googleusercontent.com/aida-public/AB6AXuBva-bWYM8-1wlk5vmfrdBC_xB5B8eh2h07U-8tZNguNKTEEzSuO0akNT4eScOJ8KWGyWoIA8fl8l79h2IcwfLJ-FPvEtSVjpLCB6nTHJa-q9m5R3e-0LfaQDTORE6tM2ZJ3SASCgK4taycUhOg1qauKCQYGSNuMeA-KuUBhfpCP0A--MEe-3K5JnEQWmgt8MDlGueb5DXypB0g56tfSX47KG6UnCJT-azSbu0RaZRJThrEg83oBtBgIwBjz5RTc3gXWm0-BOtd1dE'
  const heroSlideDurationMs = useMemo(() => {
    const raw = Number(heroSettings?.slideDurationSeconds)
    if (Number.isFinite(raw) && raw > 0) return raw * 1000
    return 5500
  }, [heroSettings?.slideDurationSeconds])
  const heroMediaDurations = useMemo(() => {
    if (heroSettings?.mediaDurations && typeof heroSettings.mediaDurations === 'object') {
      return heroSettings.mediaDurations
    }
    return {}
  }, [heroSettings?.mediaDurations])
  const heroMedia = useMemo(() => {
    const items = [
      ...heroImages.map((src) => {
        const raw = Number(heroMediaDurations[src])
        const durationMs = Number.isFinite(raw) && raw > 0 ? raw * 1000 : heroSlideDurationMs
        return { type: 'image', src, durationMs }
      }),
      ...heroVideos.map((video) => {
        const embedUrl = buildEmbedUrl(video)
        if (!embedUrl) return null
        const raw = Number(heroMediaDurations[video])
        const durationMs = Number.isFinite(raw) && raw > 0 ? raw * 1000 : heroSlideDurationMs
        return { type: 'video', src: embedUrl, durationMs }
      })
    ].filter(Boolean)
    if (items.length === 0) return [{ type: 'image', src: heroFallbackImage, durationMs: heroSlideDurationMs }]
    return items
  }, [heroFallbackImage, heroImages, heroMediaDurations, heroSlideDurationMs, heroVideos])
  const [heroMediaIndex, setHeroMediaIndex] = useState(0)
  const heroMediaCount = heroMedia.length
  const handleHeroPrev = () => {
    if (heroMediaCount <= 1) return
    setHeroMediaIndex(prev => (prev - 1 + heroMediaCount) % heroMediaCount)
  }
  const handleHeroNext = () => {
    if (heroMediaCount <= 1) return
    setHeroMediaIndex(prev => (prev + 1) % heroMediaCount)
  }

  useEffect(() => {
    setHeroMediaIndex(0)
  }, [heroMediaCount])

  useEffect(() => {
    if (heroMediaCount <= 1) return
    const duration = heroMedia[heroMediaIndex]?.durationMs ?? heroSlideDurationMs
    const id = setTimeout(() => {
      setHeroMediaIndex(prev => (prev + 1) % heroMediaCount)
    }, duration)
    return () => clearTimeout(id)
  }, [heroMedia, heroMediaCount, heroMediaIndex, heroSlideDurationMs])

  return (
    <div className="home-container">
      
      {/* Hero Section */}
      <section className="home-hero-section">
        <div className="home-hero-container">
          <div className="home-hero-bg">
            <div className="home-hero-carousel">
              <div
                className="home-hero-carousel-track"
                style={{ transform: `translateX(-${heroMediaIndex * 100}%)` }}
              >
                {heroMedia.map((item, index) => (
                  <div key={`${item.type}-${item.src}-${index}`} className="home-hero-carousel-item">
                    {item.type === 'video' ? (
                      index === heroMediaIndex ? (
                        <iframe
                          src={addAutoplayParams(item.src)}
                          title={`Hero video ${index + 1}`}
                          className="home-hero-carousel-video"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          loading="lazy"
                        />
                      ) : (
                        <div className="home-hero-carousel-placeholder" />
                      )
                    ) : (
                      <img src={item.src} alt={`Hero ${index + 1}`} className="home-hero-carousel-image" />
                    )}
                  </div>
                ))}
              </div>
              {heroMedia.length > 1 && (
                <div className="home-hero-carousel-dots">
                  {heroMedia.map((_, idx) => (
                    <button
                      key={`hero-dot-${idx}`}
                      type="button"
                      className={`home-hero-carousel-dot ${idx === heroMediaIndex ? 'active' : ''}`}
                      onClick={() => setHeroMediaIndex(idx)}
                      aria-label={`Hero ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
              {heroMedia.length > 1 && (
                <>
                  <button
                    type="button"
                    className="home-hero-carousel-arrow home-hero-carousel-arrow-left"
                    onClick={handleHeroPrev}
                    aria-label="Anterior"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="home-hero-carousel-arrow home-hero-carousel-arrow-right"
                    onClick={handleHeroNext}
                    aria-label="Siguiente"
                  >
                    ›
                  </button>
                </>
              )}
            </div>
            <div className="home-hero-overlay"></div>
            <div className="home-hero-content">
              <h1>{heroSettings?.title || 'Elegancia Natural para tu Hogar'}</h1>
              <h2>{heroSettings?.subtitle || 'Muebles de pino sostenibles y hechos a mano, diseñados para durar toda la vida. Lleva la calidez de la naturaleza a tu interior con nuestra nueva colección.'}</h2>
              <div className="home-hero-actions">
                <button 
                  className="btn-primary"
                  onClick={() => navigate(heroSettings?.buttonLink || '/productos')}
                >
                  {heroSettings?.buttonText || 'Explorar Colección'}
                </button>
              </div>
            </div>
          </div>
        </div>
        {(heroImages.length > 0 || heroVideos.length > 0) && (
          <div className="home-hero-media">
            <div className="home-hero-media-grid">
              {heroImages.map((image, index) => (
                <div key={`hero-img-${image}-${index}`} className="home-hero-media-item">
                  <img src={image} alt={`Hero ${index + 1}`} />
                </div>
              ))}
              {heroVideos.map((video, index) => {
                const embedUrl = buildEmbedUrl(video)
                if (!embedUrl) return null
                return (
                  <div key={`hero-video-${video}-${index}`} className="home-hero-media-item home-hero-media-video">
                    <iframe
                      src={embedUrl}
                      title={`Video ${index + 1}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </section>

      {/* Categories Section (Horizontal Scroll) */}
      <section className="home-categories-section">
        <div className="home-categories-list">
          <button 
            className="category-pill active"
            onClick={() => navigate('/productos')}
          >
            Todos
          </button>
          {categories.map(cat => (
            <button 
              key={cat.id} 
              className="category-pill"
              onClick={() => handleCategoryClick(cat)}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </section>

      {/* New Arrivals Section */}
      <section className="home-section">
        <div className="section-header">
          <div className="section-title-group">
            <h3>Recién Llegados</h3>
            <p>Frescos del taller. Perfección artesanal.</p>
          </div>
          <a href="#" onClick={(e) => { e.preventDefault(); navigate('/productos'); }} className="link-arrow">
            Ver todos los productos
            <span className="material-symbols-outlined">arrow_forward</span>
          </a>
        </div>

        {productsLoading ? (
           <div className="loading-state">Cargando productos...</div>
        ) : (
          <div className="products-scroll-container">
            {newArrivals.map(product => (
              <div key={product.id} className="product-card-vertical" onClick={() => navigate(`/productos/${product.id}`)}>
                <div className="product-image-container">
                  <VariantMediaCarousel
                    media={getVariantMedia(product, { width: 600, height: 800, crop: 'fill' })}
                    alt={product.name || 'Producto'}
                  />
                  <button className="btn-icon-float">
                    <span className="material-symbols-outlined">add_shopping_cart</span>
                  </button>
                </div>
                <div className="product-info">
                  <div className="product-header">
                    <p className="product-title">{product.name}</p>
                    <p className="product-price">${product.price}</p>
                  </div>
                  <p className="product-category">{product.categoryName || 'Colección'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Offers Section */}
      <section className="home-section bg-accent">
        <div className="max-width-container">
          <div className="section-header">
            <div className="section-title-group">
              <h3 className="flex-center">
                <span className="material-symbols-outlined text-primary">local_offer</span>
                Ofertas por Tiempo Limitado
              </h3>
              <p>No te pierdas estas ofertas exclusivas.</p>
            </div>
            <a href="#" onClick={(e) => { e.preventDefault(); navigate('/productos'); }} className="link-arrow">
              Ver todas las ofertas
              <span className="material-symbols-outlined">arrow_forward</span>
            </a>
          </div>

          <div className="products-grid">
            {offers.map(product => (
              <div key={product.id} className="product-card-simple" onClick={() => navigate(`/productos/${product.id}`)}>
                <div className="product-image-simple-container">
                  <div className="discount-badge">-15%</div>
                  <VariantMediaCarousel
                    media={getVariantMedia(product, { width: 600, height: 600, crop: 'fill' })}
                    alt={product.name || 'Producto'}
                  />
                </div>
                <h4 className="product-title-simple">{product.name}</h4>
                <div className="product-price-row">
                  <span className="price-current">${(product.price * 0.85).toFixed(2)}</span>
                  <span className="price-original">${product.price}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curated Collections Section */}
      <section className="py-14 px-4 md:px-10">
        <div className="flex flex-col gap-8 max-w-[1280px] mx-auto">
          <div className="flex flex-col gap-2 text-center max-w-2xl mx-auto">
            <h3 className="text-3xl font-medium text-text-dark dark:text-text-light tracking-tight">Colecciones Curadas</h3>
            <p className="text-gray-600 dark:text-gray-400 text-lg font-light tracking-tight">Encuentra la estética perfecta para cada habitación de tu casa.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-auto md:h-[500px]">
            <div className="group relative overflow-hidden rounded-xl h-[300px] md:h-full cursor-pointer" onClick={() => navigate('/productos?categoria=nordico')}>
              <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors z-10"></div>
              <div className="w-full h-full bg-cover bg-center transition-transform duration-700 group-hover:scale-110" data-alt="Bright living room with pine furniture" style={{backgroundImage: 'url("https://lh3.googleusercontent.com/aida-public/AB6AXuAO5wpR3yC50wh6ezzd9QGucfMcR7d077anoaUR4gZhGbdpxdfbS4RVQxb3fuOda2SAwp6ehxvPQGnPI1II3D91KSYwhF1viPgk9TxJMiQ-dzI0Av_h_0nIW7JxDGfOAINO-TI6UpxKzM8-zislFAhxRCQHG_dZ9VY1Y05_6bxiUDgvUmHsSUpzCKd1V21qqS8i6_Lz8SAhOhQLbDEO4D06wlagGZZ0M_18EPt4S3-kkPE5RkTf41D9rLd-VAyfGpu57HQX8sV2DIE")'}}></div>
              <div className="absolute bottom-6 left-6 z-20 text-white">
                <h4 className="text-2xl font-medium mb-2 tracking-tight">Estilo Nórdico</h4>
                <span className="inline-flex items-center text-sm font-medium border-b border-white pb-1 tracking-tight">Comprar Look <span className="material-symbols-outlined text-[16px] ml-1">arrow_forward</span></span>
              </div>
            </div>
            <div className="flex flex-col gap-6 h-full">
              <div className="group relative overflow-hidden rounded-xl flex-1 cursor-pointer" onClick={() => navigate('/productos?categoria=rustico')}>
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors z-10"></div>
                <div className="w-full h-full bg-cover bg-center transition-transform duration-700 group-hover:scale-110" data-alt="Cozy dining area with wooden table" style={{backgroundImage: 'url("https://lh3.googleusercontent.com/aida-public/AB6AXuA1KXeQTXaOYfgplFIuIXxXidI5B1OwyERDOvzGWXX5fBF6RM613N3z0HvbblcQSCaHNSrzSWcVkltOVVsj1ter70_g_2ISffbK_kpcumyCVugyxS1c6mbZ63H8feTFYHcLREXFElTNQKRspQTuzDjMVcGIGKuCvmpsfzhunlmLF-eWcIJsY-5r0QCID9qB_pI3T5pNxPfHnF6K7fqWosxb0maMxMfsTOgNwBHe6nStW_UU8KZCNbhRhbpwVBcuaQxQ3OWzWD_W1zQ")'}}></div>
                <div className="absolute bottom-6 left-6 z-20 text-white">
                  <h4 className="text-xl font-medium mb-1 tracking-tight">Comedor Rústico</h4>
                  <span className="inline-flex items-center text-sm font-medium border-b border-white pb-1 tracking-tight">Comprar Look <span className="material-symbols-outlined text-[16px] ml-1">arrow_forward</span></span>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-xl flex-1 cursor-pointer" onClick={() => navigate('/productos?categoria=oficina')}>
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors z-10"></div>
                <div className="w-full h-full bg-cover bg-center transition-transform duration-700 group-hover:scale-110" data-alt="Organized home office with pine desk" style={{backgroundImage: 'url("https://lh3.googleusercontent.com/aida-public/AB6AXuDE9KOMPY2zAZoFPdm-JDIrKXnmHc5VTIpP6DaL1y8teZ0gXBgJ1TimasuZ9Epgt3uhyHlgvInUA4FVKhLoBXwqk0ZDbSxe827EIDKzjVfV1DgbbNJ0mqynZ4HMndCypAmyzJXkhdmjpDqxDUrK-dUvKSBHXv8pwADqhcFTErQBRNU9L3PPy8-PBzdRZz1UaU7dPB_mwABW6q8N-c_q5ZMUHHSBAqovz8BIsOqzF4QXnx6h-V3FXFdDOeFgMQWJSOaldHruaX2hDnM")'}}></div>
                <div className="absolute bottom-6 left-6 z-20 text-white">
                  <h4 className="text-xl font-medium mb-1 tracking-tight">Oficina Moderna</h4>
                  <span className="inline-flex items-center text-sm font-medium border-b border-white pb-1 tracking-tight">Comprar Look <span className="material-symbols-outlined text-[16px] ml-1">arrow_forward</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  )
}

export default Home
